import {configureStore, combineReducers, Reducer, ReducersMapObject} from '@reduxjs/toolkit';
import {createEpicMiddleware} from 'redux-observable';

import {batchMiddleware} from './middlewares';
import {epicDeps, thunkExtra} from './deps';
import {createRootEpic} from './utils';
import type {WikiEpic, WikiState} from './types';

const rootReducers = {} as ReducersMapObject<WikiState>;
const rootEpics: WikiEpic[] = [];

let INSTANCE: ReturnType<typeof createStore> | null = null;

export type WikiStore = ReturnType<typeof createStore>['store'];

export function getStore() {
    if (!INSTANCE) {
        INSTANCE = createStore();
    }
    return INSTANCE.store;
}

export function inject<K extends keyof WikiState>(
    key: K,
    reducer: Reducer<WikiState[K]>,
    epic?: WikiEpic,
) {
    // @ts-expect-error
    rootReducers[key] = reducer;

    if (!INSTANCE) {
        if (epic) rootEpics.push(epic);
        return;
    }

    const {store, epicMiddleware} = INSTANCE;
    store.replaceReducer(combineReducers(rootReducers));
    if (epic) epicMiddleware.run(createRootEpic(epic));
}

function createStore() {
    const epicMiddleware = createEpicMiddleware({
        dependencies: epicDeps,
    });

    const store = configureStore({
        reducer: rootReducers,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: {
                    extraArgument: thunkExtra,
                },
            }).concat(epicMiddleware, batchMiddleware),
        preloadedState: window.__STATE__ || {},
        devTools: process.env.NODE_ENV !== 'production',
    });

    epicMiddleware.run(createRootEpic(...rootEpics));
    rootEpics.length = 0;

    return {store, epicMiddleware};
}
